import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { INPUT, PRIMARY, SECONDARY, LINK, ALERT } from '../auth/mfaStyles';

const STEP_LABEL = { scan: 'Step 1 of 3: add FoodOps to your authenticator app',
  confirm: 'Step 2 of 3: enter the code from the app', codes: 'Step 3 of 3: save your recovery codes' };
// Defensive: an enrol response without a secret used to throw here and take the
// whole sign-in screen down with it. Show nothing rather than crashing.
const groupInFours = (key) => (typeof key === 'string' ? key.replace(/\s+/g, '').match(/.{1,4}/g) || [] : []).join(' ');

async function mfaPost(apiUrl, authHeader, path, body) {
  const res = await fetch(`${apiUrl}/api/auth/mfa/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authHeader}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data, detail: typeof data?.detail === 'string' ? data.detail : '' };
}

function downloadCodes(codes) {
  if (typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([`FoodOps recovery codes\n\n${codes.join('\n')}\n`], { type: 'text/plain' }));
  const link = Object.assign(document.createElement('a'), { href: url, download: 'foodops-recovery-codes.txt' });
  link.click();
  URL.revokeObjectURL(url);
}

// Turns two-factor on. authHeader is a session token or the enrolment-only token from login. Secrets stay in memory.
export default function MfaEnrol({ authHeader, apiUrl = '', onDone, onCancel }) {
  const [step, setStep] = useState('scan');
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(null);
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const session = useRef(null);
  const focusTarget = useRef(null);
  const started = useRef(false);
  const mounted = useRef(false);

  useEffect(() => { focusTarget.current?.focus(); }, [step, setup, error]);
  // Enrol once per mount: a second enroll call replaces the pending secret on the server, so the key on screen would
  // no longer match (React StrictMode runs effects twice in development).
  useEffect(() => {
    mounted.current = true;
    if (!started.current) {
      started.current = true;
      mfaPost(apiUrl, authHeader, 'enroll').then((r) => {
        if (!mounted.current) return;
        if (r.ok && typeof r.data?.secret === 'string' && r.data.secret) {
          setSetup({ secret: r.data.secret, uri: r.data.otpauth_uri });
        } else {
          setError(r.detail || 'Could not start two-factor set-up.');
        }
      }).catch(() => mounted.current && setError('Could not reach the server. Try again.'));
    }
    return () => { mounted.current = false; session.current = null; };
  }, [apiUrl, authHeader]);

  const confirm = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError('');
    const r = await mfaPost(apiUrl, authHeader, 'confirm', { code: code.trim() }).catch(() => ({ detail: 'Could not reach the server. Try again.' }));
    setBusy(false);
    setCode('');
    if (!r.ok) { setError(r.detail || 'That code is not right'); return; }
    session.current = r.data.session || null;
    setSetup(null);
    setCodes(r.data.recovery_codes || []);
    setStep('codes');
  };

  const done = () => {
    const result = session.current;
    session.current = null;
    setCodes(null);
    setStep('done');
    onDone(result);
  };

  if (step === 'done') return null;
  return (
    <section className="space-y-5" data-testid="mfa-enrol">
      <p aria-live="polite" className="text-sm font-medium text-gray-900" data-testid="mfa-enrol-step">{STEP_LABEL[step]}</p>
      {error && <div role="alert" className={ALERT} data-testid="mfa-enrol-error">{error}</div>}
      {step === 'scan' && setup && (
        <div className="space-y-4">
          <div className="flex justify-center" data-testid="mfa-enrol-qr"><QRCodeSVG value={setup.uri} size={176} /></div>
          <p className="text-sm text-gray-600">Can&apos;t scan? Enter this key</p>
          <code className="block text-base tracking-wider break-all" data-testid="mfa-enrol-secret">{groupInFours(setup.secret)}</code>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={SECONDARY} data-testid="mfa-enrol-copy-secret-btn"
              onClick={() => navigator.clipboard?.writeText(setup.secret)}>Copy key</button>
            <a href={setup.uri} className={`${LINK} inline-flex items-center`} data-testid="mfa-enrol-otpauth-link">Open in authenticator app</a>
          </div>
          <button type="button" ref={focusTarget} className={PRIMARY} data-testid="mfa-enrol-next-btn" onClick={() => setStep('confirm')}>Next</button>
        </div>
      )}
      {step === 'confirm' && (
        <form onSubmit={confirm} className="space-y-4" noValidate>
          <label htmlFor="mfa-enrol-code-input" className="block text-sm font-medium text-gray-700">6-digit code</label>
          <input id="mfa-enrol-code-input" data-testid="mfa-enrol-code-input" ref={focusTarget} className={INPUT} type="text"
            inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value)} />
          <button type="submit" disabled={busy} className={PRIMARY} data-testid="mfa-enrol-confirm-btn">{busy ? 'Checking…' : 'Confirm'}</button>
        </form>
      )}
      {step === 'codes' && codes && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Each code works once if you lose your phone. They will not be shown again.</p>
          <ul className="grid grid-cols-2 gap-2 font-mono text-base" data-testid="mfa-enrol-codes">
            {codes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <div className="flex flex-wrap gap-2">
            <button type="button" ref={focusTarget} className={SECONDARY} data-testid="mfa-enrol-download-btn" onClick={() => downloadCodes(codes)}>Download as text file</button>
            <button type="button" className={SECONDARY} data-testid="mfa-enrol-copy-codes-btn" onClick={() => navigator.clipboard?.writeText(codes.join('\n'))}>Copy</button>
          </div>
          <label className="flex items-center gap-3 min-h-[44px] text-sm text-gray-800">
            <input type="checkbox" className="w-5 h-5" checked={saved} onChange={(e) => setSaved(e.target.checked)} data-testid="mfa-enrol-saved-checkbox" />
            I have saved these codes
          </label>
          <button type="button" disabled={!saved} className={PRIMARY} data-testid="mfa-enrol-done-btn" onClick={done}>Done</button>
        </div>
      )}
      {onCancel && step !== 'codes' && (
        <button type="button" className={LINK} data-testid="mfa-enrol-cancel-btn" onClick={onCancel}>Cancel</button>
      )}
    </section>
  );
}
